import React, { useEffect, useState } from "react";
import { Rate, Input, message } from "antd";
import Axios from "../../services/AxiosInterceptor";
import { Calendar, MapPin, Clock, User, Package, Star, Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const { TextArea } = Input;

const STATUS_PALETTE = {
  CONFIRMED: { bg: "rgba(34,160,107,0.1)", fg: "#22a06b", label: "Confirmed", icon: CheckCircle2 },
  PENDING: { bg: "rgba(245,182,66,0.12)", fg: "#b27e16", label: "Pending", icon: AlertCircle },
  REJECTED: { bg: "rgba(233,111,88,0.1)", fg: "#d94a33", label: "Rejected", icon: XCircle },
};

const Mybooking = () => {
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [singlebooking, setSingleBooking] = useState("");

  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        const response = await Axios.post("api/get_booking_data", { id: userId });
        const reverse = response.data.data.reverse();
        setBookings(reverse);
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const submitRating = async () => {
    if (!comment || !rating) {
      message.error("Please enter comment and rating.");
      return;
    }
    const userId = localStorage.getItem("userId");
    const ratingData = {
      serviceId: singlebooking?.serviceId,
      resourceId: singlebooking?.resource,
      userId,
      message: comment,
      rating: Number(rating),
      date: new Date().toISOString(),
    };
    try {
      const response = await Axios.post("/api/cx_rating", { data: ratingData });
      if (response.data.success) {
        message.success("Rating submitted successfully!");
        setIsModalOpen(false);
        setComment("");
        setRating(0);
      } else {
        message.error("Something went wrong.");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      message.error("Error submitting rating");
    }
  };

  const handleRating = (booking) => {
    setSingleBooking(booking);
    if (booking?.serviceStatus?.trim().toLowerCase() === "completed") {
      setModalMessage(`You are rating resource and yoga service ${booking.resource}`);
      setIsModalOpen(true);
    } else {
      setIsWarningModalOpen(true);
    }
  };

  const today = new Date().setHours(0, 0, 0, 0);

  const upcomingBookings = bookings.filter(
    (b) => new Date(b.date).setHours(0, 0, 0, 0) >= today
  );

  const completedBookings = bookings.filter(
    (b) => b.serviceStatus?.trim().toLowerCase() === "completed"
  );

  const getActiveData = () => {
    if (activeTab === "upcoming") return upcomingBookings;
    if (activeTab === "completed") return completedBookings;
    return bookings;
  };

  const tabs = [
    { key: "all", label: "All", count: bookings.length },
    { key: "upcoming", label: "Upcoming", count: upcomingBookings.length },
    { key: "completed", label: "Completed", count: completedBookings.length },
  ];

  const renderStatusPill = (status) => {
    const p = STATUS_PALETTE[status] || { bg: "rgba(38,66,54,0.06)", fg: "var(--ym-muted)", label: status, icon: AlertCircle };
    const Icon = p.icon;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: "var(--ym-radius)", background: p.bg, color: p.fg, fontSize: 12, fontWeight: 700 }}>
        <Icon size={12} />
        {p.label}
      </span>
    );
  };

  const TH = ({ children }) => (
    <th style={{ padding: "12px 14px", fontSize: 12, fontWeight: 700, color: "var(--ym-muted)", textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "left", borderBottom: "1px solid var(--ym-line)", whiteSpace: "nowrap", background: "rgba(38,66,54,0.03)" }}>
      {children}
    </th>
  );

  const TD = ({ children, style }) => (
    <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--ym-ink)", borderBottom: "1px solid var(--ym-line)", verticalAlign: "middle", ...style }}>
      {children}
    </td>
  );

  const Row = ({ booking }) => {
    return (
      <tr style={{ transition: "background 0.15s" }} className="myb-row">
        <TD><span style={{ fontWeight: 700 }}>{booking.serviceName}</span></TD>
        <TD>{booking.location}</TD>
        <TD style={{ whiteSpace: "nowrap" }}>{new Date(booking.date).toLocaleDateString()}</TD>
        <TD>{booking.time}</TD>
        <TD style={{ textAlign: "center" }}>{booking.seatsBooked}</TD>
        <TD style={{ fontWeight: 700, whiteSpace: "nowrap" }}>₹{booking.amountPaid}</TD>
        <TD><span style={{ fontSize: 11, color: "var(--ym-muted)", fontFamily: "monospace" }}>{(booking.transactionId || "").slice(0, 12)}...</span></TD>
        <TD>{renderStatusPill(booking.paymentStatus)}</TD>
        <TD>{booking.serviceStatus}</TD>
        <TD>{booking.resource || "-"}</TD>
        <TD style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          {booking.subscriptionStart
            ? `${new Date(booking.subscriptionStart).toLocaleDateString()} - ${new Date(booking.subscriptionEnd || "").toLocaleDateString()}`
            : "-"}
        </TD>
        <TD>
          <button
            disabled={booking.ratingStatus === true}
            onClick={() => handleRating(booking)}
            className={`ym-button ${booking.ratingStatus ? "" : "ym-button-gold"}`}
            style={{ padding: "4px 12px", minHeight: 0, fontSize: 12, minWidth: 70, justifyContent: "center", opacity: booking.ratingStatus ? 0.5 : 1 }}
          >
            {booking.ratingStatus ? "Rated" : "Rate"}
          </button>
        </TD>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <Package size={20} style={{ color: "var(--ym-gold)" }} />
        <h3 className="ym-service-title" style={{ fontSize: 18, margin: 0 }}>My Bookings</h3>
      </div>

      {/* Custom Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap" }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 18px",
              border: "1px solid var(--ym-line)",
              borderRadius: "var(--ym-radius)",
              background: activeTab === tab.key ? "linear-gradient(135deg, rgba(255,248,237,0.9), rgba(255,253,247,0.95))" : "transparent",
              color: activeTab === tab.key ? "var(--ym-sage)" : "var(--ym-muted)",
              fontWeight: activeTab === tab.key ? 700 : 600,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all 0.2s",
              borderBottom: activeTab === tab.key ? "2px solid var(--ym-gold)" : "1px solid var(--ym-line)",
            }}
            onMouseEnter={(e) => { if (activeTab !== tab.key) e.currentTarget.style.background = "rgba(38,66,54,0.04)"; }}
            onMouseLeave={(e) => { if (activeTab !== tab.key) e.currentTarget.style.background = "transparent"; }}
          >
            {tab.label}
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 20,
              height: 20,
              borderRadius: 10,
              background: activeTab === tab.key ? "var(--ym-gold)" : "rgba(38,66,54,0.08)",
              color: activeTab === tab.key ? "#20160d" : "var(--ym-muted)",
              fontSize: 11,
              fontWeight: 800,
              padding: "0 6px",
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader2 size={28} style={{ animation: "spin 0.8s linear infinite", color: "var(--ym-sage)" }} />
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="myb-table-wrap">
            <div className="ym-card" style={{ overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                {getActiveData().length > 0 ? (
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                    <thead>
                      <tr>
                        <TH>Service</TH>
                        <TH>Location</TH>
                        <TH>Date</TH>
                        <TH>Time</TH>
                        <TH style={{ textAlign: "center" }}>Seats</TH>
                        <TH>Amount</TH>
                        <TH>TX ID</TH>
                        <TH>Payment</TH>
                        <TH>Status</TH>
                        <TH>Resource</TH>
                        <TH>Subscription</TH>
                        <TH>Rating</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {getActiveData().map((booking, i) => (
                        <Row key={booking._id || i} booking={booking} />
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--ym-muted)", fontSize: 14 }}>
                    <Package size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
                    <p style={{ margin: 0, fontWeight: 600 }}>No bookings found in this category.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="myb-mobile-cards">
            {getActiveData().length > 0 ? (
              <div style={{ display: "grid", gap: 14 }}>
                {getActiveData().map((booking, index) => {
                  const p = STATUS_PALETTE[booking.paymentStatus] || {};
                  const StatusIcon = p.icon || AlertCircle;
                  const dateStr = new Date(booking.date).toDateString();
                  return (
                    <div className="ym-card" key={booking._id || index} style={{ padding: 18 }}>
                      {/* Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--ym-ink)", lineHeight: 1.3, flex: 1 }}>{booking.serviceName}</h4>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: "var(--ym-radius)", background: p.bg || "rgba(38,66,54,0.06)", color: p.fg || "var(--ym-muted)", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
                          <StatusIcon size={11} />
                          {p.label || booking.paymentStatus}
                        </span>
                      </div>

                      {/* Details */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--ym-muted)", fontSize: 13 }}>
                          <Calendar size={14} style={{ color: "var(--ym-sage)", flexShrink: 0 }} />
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dateStr}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--ym-muted)", fontSize: 13 }}>
                          <Clock size={14} style={{ color: "var(--ym-sage)", flexShrink: 0 }} />
                          <span>{booking.time}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--ym-muted)", fontSize: 13, gridColumn: "span 2" }}>
                          <MapPin size={14} style={{ color: "var(--ym-sage)", flexShrink: 0 }} />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{booking.location}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--ym-muted)", fontSize: 13, gridColumn: "span 2" }}>
                          <User size={14} style={{ color: "var(--ym-sage)", flexShrink: 0 }} />
                          <span>{booking.resource || "No Resource"}</span>
                        </div>
                      </div>

                      {/* Price & Seats */}
                      <div style={{ background: "rgba(255,248,237,0.5)", padding: "12px", borderRadius: "var(--ym-radius)", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, border: "1px solid rgba(245,182,66,0.12)" }}>
                        <div>
                          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: "var(--ym-muted)" }}>Amount Paid</p>
                          <p style={{ margin: "2px 0 0", fontSize: 16, fontWeight: 800, color: "var(--ym-sage)" }}>₹{booking.amountPaid}</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: "var(--ym-muted)" }}>Seats</p>
                          <p style={{ margin: "2px 0 0", fontSize: 16, fontWeight: 800, color: "var(--ym-ink)" }}>{booking.seatsBooked}</p>
                        </div>
                      </div>

                      {/* Action */}
                      <button
                        disabled={booking.ratingStatus === true}
                        onClick={() => handleRating(booking)}
                        className={`ym-button ${booking.ratingStatus ? "" : "ym-button-gold"}`}
                        style={{ width: "100%", justifyContent: "center", opacity: booking.ratingStatus ? 0.5 : 1, minHeight: 44 }}
                      >
                        {booking.ratingStatus ? "Already Rated" : "Rate Service"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ym-card" style={{ textAlign: "center", padding: "60px 24px" }}>
                <Package size={36} style={{ marginBottom: 12, opacity: 0.3, color: "var(--ym-muted)" }} />
                <p style={{ margin: 0, fontWeight: 600, color: "var(--ym-muted)" }}>No bookings found in this category.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Rating Modal */}
      {isModalOpen && (
        <div className="myb-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="myb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ym-card" style={{ padding: 0, maxWidth: 440, width: "90vw" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--ym-line)" }}>
                <h4 className="ym-service-title" style={{ margin: 0, fontSize: 16 }}>Rate Service</h4>
              </div>
              <div style={{ padding: "20px 24px" }}>
                <p style={{ fontWeight: 600, marginBottom: 16, color: "var(--ym-ink)" }}>{modalMessage}</p>
                <div style={{ marginBottom: 16 }}>
                  <label className="ym-label" style={{ marginBottom: 6, display: "block" }}>Rating</label>
                  <Rate value={rating} onChange={setRating} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label className="ym-label" style={{ marginBottom: 6, display: "block" }}>Comment</label>
                  <TextArea rows={4} placeholder="Leave a comment" maxLength={100} showCount value={comment} onChange={(e) => setComment(e.target.value)} style={{ borderRadius: "var(--ym-radius)", borderColor: "var(--ym-line)", fontFamily: "inherit" }} />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setIsModalOpen(false)} className="ym-button ym-button-ghost" style={{ display: "flex", alignItems: "center", gap: 6 }}>Cancel</button>
                  <button onClick={submitRating} className="ym-button ym-button-gold" style={{ display: "flex", alignItems: "center", gap: 6 }}><Star size={15} /> Submit Rating</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      {isWarningModalOpen && (
        <div className="myb-overlay" onClick={() => setIsWarningModalOpen(false)}>
          <div className="myb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ym-card" style={{ padding: 0, maxWidth: 400, width: "90vw" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--ym-line)" }}>
                <h4 className="ym-service-title" style={{ margin: 0, fontSize: 16 }}>Cannot Rate Service</h4>
              </div>
              <div style={{ padding: "20px 24px" }}>
                <p style={{ color: "var(--ym-ink)", marginBottom: 20 }}>You cannot rate the service until it is completed.</p>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={() => setIsWarningModalOpen(false)} className="ym-button ym-button-primary">OK</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mybooking;
